import { RefObject, useLayoutEffect, useState } from 'react';

interface UseCollapseSpacerParams {
  /** The page/inner scroll container that drives the collapse. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Scroll distance needed to fully collapse the header (expanded − collapsed px). */
  collapseDistancePx: number;
  /** Value(s) that change when content height changes (e.g. row count), to re-measure. */
  contentDep: unknown;
}

/**
 * The bottom spacer that guarantees enough scroll travel to fully collapse a
 * `CollapsibleHeaderSection` is only needed when the content is too short to
 * scroll on its own; otherwise it's dead space at the bottom. This renders it by
 * default, measures whether the rest of the content already overflows, and drops
 * it if so.
 *
 * Self-stabilizing, not an infinite loop: `check` subtracts the spacer's own
 * height when present, so `contentHeight` reflects the true content height
 * regardless of the current toggle state and settles on a single value.
 */
export const useCollapseSpacer = ({
  scrollRef,
  collapseDistancePx,
  contentDep,
}: UseCollapseSpacerParams): boolean => {
  const [needsCollapseSpacer, setNeedsCollapseSpacer] = useState(true);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;
    const check = () => {
      const contentHeight = node.scrollHeight - (needsCollapseSpacer ? collapseDistancePx : 0);
      setNeedsCollapseSpacer(contentHeight < node.clientHeight + collapseDistancePx);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [scrollRef, collapseDistancePx, needsCollapseSpacer, contentDep]);

  return needsCollapseSpacer;
};
